import { useEffect } from 'react';
import { Box, Button, Flex, Stack } from '@chakra-ui/react';
import { Formiz, useForm } from '@formiz/core';
import { isEmail, isMaxLength, isMinLength } from '@formiz/validations';
import { useTranslation } from 'react-i18next';

import { useCreateAccount } from '@/app/account/account.service';
import { FieldInput, SlideIn, FieldRadios } from '@/components';
import { PUBLIC_SECTORS_OPTIONS } from '@/domain/user';

import type { FC } from 'react';
import type { CreateableByClientObj, Sector } from '@/domain/user';

export type PublicRegisterFormProps = AuxProps & {
  defaultSector?: Sector;
  onSuccess: TODO;
  onError: TODO;
};

export const PublicRegisterForm: FC<PublicRegisterFormProps> = ({
  defaultSector = 'ADMIN',
  onError,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const form = useForm();

  /* useEffect(() => {
   *   i18n.changeLanguage(form.values?.langKey);
   * }, [i18n, form.values?.langKey]); */

  const {
    mutate: registerUser,
    isLoading,
  } = useCreateAccount({
    onError,
    onSuccess,
  });

  const handleSubmit = (
    values: CreateableByClientObj & { password: string }
  ) => {
    if (defaultSector && !values.sector) {
      values.sector = defaultSector;
    }
    registerUser(values);
  };

  const passwordValidations = [
    {
      rule: isMinLength(4),
      message: t('account:data.password.tooShort', { min: 4 }),
    },
    {
      rule: isMaxLength(50),
      message: t('account:data.password.tooLong', { max: 50 }),
    },
  ];

  const initialValues = {
    sector: 'PER',
  };

  return (
    <SlideIn>
      <Box p="2" pb="4rem" w="32rem" maxW="full" m="auto">
        <Formiz
          id="register-form"
          autoForm
          onValidSubmit={handleSubmit}
          connect={form}
          initialValues={initialValues}
        >
          <Stack spacing="4">
            <FieldRadios
              name="sector"
              direction="column"
              i18nPrefix="account:data.sector."
              label={t<string>('account:data.sector.label')}
              options={PUBLIC_SECTORS_OPTIONS}
            />
            <FieldInput
              name="email"
              label={t<string>('account:data.email.label')}
              required={t<string>('account:data.email.required')}
              validations={[
                {
                  rule: isMinLength(5),
                  message: t('account:data.email.tooShort', { min: 5 }),
                },
                {
                  rule: isMaxLength(254),
                  message: t('account:data.email.tooLong', { min: 254 }),
                },
                {
                  rule: isEmail(),
                  message: t('account:data.email.invalid'),
                },
              ]}
            />

            <FieldInput
              name="password"
              type="password"
              label={t<string>('account:data.password.label')}
              required={t<string>('account:data.password.required')}
              validations={passwordValidations}
            />
            <FieldInput
              name="confirmPassword"
              type="password"
              label={t<string>('account:data.confirmNewPassword.label')}
              required={t<string>('account:data.confirmNewPassword.required') as string}
              validations={[
                ...passwordValidations,
                {
                  rule: (value) => value === form?.values?.password,
                  message: t('account:data.confirmNewPassword.notEqual'),
                  deps: [form?.values?.password],
                },
              ]}
            />
            <Flex>
              <Button
                isLoading={isLoading}
                isDisabled={form.isSubmitted && !form.isValid}
                type="submit"
                variant="@primary"
                ms="auto"
              >
                {t<string>('account:register.actions.create')}
              </Button>
            </Flex>
          </Stack>
        </Formiz>
      </Box>
    </SlideIn>
  );
};

export default PublicRegisterForm;
